import { useState, useEffect, useCallback, useMemo } from 'react'
import { useGet, usePost } from '../utils/apiComponent/useApi'
import { apiClient } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { showSuccessToast, showErrorToast } from '../utils/toast'

/**
 * Hook pour s'abonner à la newsletter (public)
 */
export function useNewsletterSubscription() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { execute: subscribeApi, loading: apiLoading } = usePost(
    endpoints.subscribeNewsletter,
    { immediate: false, isPublicRoute: true }
  )

  useEffect(() => {
    setLoading(apiLoading)
  }, [apiLoading])

  const subscribe = useCallback(async (subscriptionData) => {
    setLoading(true)
    setError(null)

    try {
      const response = await subscribeApi({
        email: subscriptionData.email,
        fullName: subscriptionData.fullName || undefined,
        source: subscriptionData.source || 'website'
      })

      const successMessage = response?.messages?.[0] || 'Vous êtes maintenant abonné à notre newsletter !'
      showSuccessToast({ message: successMessage })
      
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de l\'abonnement. Veuillez réessayer.'
      setError(errorMessage)
      
      // Ne pas afficher d'erreur toast pour les conflits (déjà abonné)
      if (err?.statusCode !== 409) {
        showErrorToast({ message: errorMessage })
      } else {
        showErrorToast({ message: 'Cet email est déjà abonné à la newsletter' })
      }
      
      throw err
    } finally {
      setLoading(false)
    }
  }, [subscribeApi])

  return {
    subscribe,
    loading,
    error
  }
}

/**
 * Hook pour gérer les abonnés à la newsletter (admin)
 */
export function useNewsletterSubscribers(options = {}) {
  const {
    page = 1,
    limit = 10,
    search = '',
    isActive = null,
    sortBy = 'subscribedAt',
    sortOrder = 'desc',
    immediate = false
  } = options

  const [subscribers, setSubscribers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })

  const subscribersUrl = useMemo(() => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    if (search) params.append('search', search)
    if (isActive !== null) params.append('isActive', isActive.toString())
    params.append('sortBy', sortBy)
    params.append('sortOrder', sortOrder)
    return `${endpoints.getNewsletterSubscribers}?${params.toString()}`
  }, [page, limit, search, isActive, sortBy, sortOrder])

  const { data: subscribersData, loading: subscribersLoading, error: subscribersError, execute: fetchSubscribers } = useGet(
    subscribersUrl,
    { immediate }
  )

  useEffect(() => {
    setLoading(subscribersLoading)
    setError(subscribersError)
  }, [subscribersLoading, subscribersError])

  useEffect(() => {
    if (subscribersData) {
      if (subscribersData.subscribers && Array.isArray(subscribersData.subscribers)) {
        setSubscribers(subscribersData.subscribers)
        setPagination({
          page: subscribersData.page || page,
          limit: subscribersData.limit || limit,
          total: subscribersData.total || 0,
          totalPages: subscribersData.totalPages || 0
        })
      } else if (Array.isArray(subscribersData)) {
        setSubscribers(subscribersData)
      }
    }
  }, [subscribersData, page, limit])

  return {
    subscribers,
    loading,
    error,
    pagination,
    fetchSubscribers
  }
}

/**
 * Hook pour gérer les campagnes de newsletter (admin)
 */
export function useNewsletterCampaigns(options = {}) {
  const {
    page = 1,
    limit = 10,
    status = null,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    immediate = false
  } = options

  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })

  const campaignsUrl = useMemo(() => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    if (status) params.append('status', status)
    params.append('sortBy', sortBy)
    params.append('sortOrder', sortOrder)
    return `${endpoints.getNewsletterCampaigns}?${params.toString()}`
  }, [page, limit, status, sortBy, sortOrder])

  const { data: campaignsData, loading: campaignsLoading, error: campaignsError, execute: fetchCampaigns } = useGet(
    campaignsUrl,
    { immediate }
  )

  useEffect(() => {
    setLoading(campaignsLoading)
    setError(campaignsError)
  }, [campaignsLoading, campaignsError])

  useEffect(() => {
    if (campaignsData) {
      if (campaignsData.campaigns && Array.isArray(campaignsData.campaigns)) {
        setCampaigns(campaignsData.campaigns)
        setPagination({
          page: campaignsData.page || page,
          limit: campaignsData.limit || limit,
          total: campaignsData.total || 0,
          totalPages: campaignsData.totalPages || 0
        })
      } else if (Array.isArray(campaignsData)) {
        setCampaigns(campaignsData)
      }
    }
  }, [campaignsData, page, limit])

  const createCampaign = useCallback(async (campaignData) => {
    try {
      const response = await apiClient(endpoints.createNewsletterCampaign, 'POST', {
        body: {
          subject: campaignData.subject,
          content: campaignData.content,
          textContent: campaignData.textContent || undefined,
          scheduledAt: campaignData.scheduledAt || undefined
        }
      })
      const successMessage = response?.messages?.[0] || 'Campagne créée avec succès'
      showSuccessToast({ message: successMessage })
      fetchCampaigns()
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de la création de la campagne'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchCampaigns])

  const sendCampaign = useCallback(async (campaignId) => {
    try {
      const response = await apiClient(endpoints.sendNewsletterCampaign(campaignId), 'POST', {})
      const successMessage = response?.messages?.[0] || 'Campagne envoyée avec succès'
      showSuccessToast({ message: successMessage })
      fetchCampaigns()
      return response?.data || response
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de l\'envoi de la campagne'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchCampaigns])

  const deleteCampaign = useCallback(async (campaignId) => {
    try {
      await apiClient(endpoints.deleteNewsletterCampaign(campaignId), 'DELETE', {})
      showSuccessToast({ message: 'Campagne supprimée avec succès' })
      fetchCampaigns()
    } catch (err) {
      const errorMessage = err?.message || 'Erreur lors de la suppression de la campagne'
      showErrorToast({ message: errorMessage })
      throw err
    }
  }, [fetchCampaigns])

  return {
    campaigns,
    loading,
    error,
    pagination,
    fetchCampaigns,
    createCampaign,
    sendCampaign,
    deleteCampaign
  }
}

/**
 * Hook pour récupérer une campagne spécifique
 */
export function useNewsletterCampaign(campaignId, options = {}) {
  const { immediate = false } = options
  const [campaign, setCampaign] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: campaignData, loading: campaignLoading, error: campaignError, execute: fetchCampaign } = useGet(
    campaignId ? endpoints.getNewsletterCampaign(campaignId) : null,
    { immediate: immediate && !!campaignId }
  )

  useEffect(() => {
    setLoading(campaignLoading)
    setError(campaignError)
  }, [campaignLoading, campaignError])

  useEffect(() => {
    if (campaignData) {
      setCampaign(campaignData)
    }
  }, [campaignData])

  return {
    campaign,
    loading,
    error,
    fetchCampaign
  }
}

/**
 * Hook pour récupérer les statistiques de newsletter
 */
export function useNewsletterStats(options = {}) {
  const { immediate = false } = options
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: statsData, loading: statsLoading, error: statsError, execute: fetchStats } = useGet(
    endpoints.getNewsletterStats,
    { immediate }
  )

  useEffect(() => {
    setLoading(statsLoading)
    setError(statsError)
  }, [statsLoading, statsError])

  useEffect(() => {
    if (statsData) {
      setStats(statsData)
    }
  }, [statsData])

  return {
    stats,
    loading,
    error,
    fetchStats
  }
}
